import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

export default function Roadmap() {
  const { t } = useTranslation();
  const phases = t("roadmap.phases", { returnObjects: true }) || [];

  return (
    <section id="roadmap" data-testid="roadmap-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl"
        >
          <p className="font-mono-alt text-[10px] tracking-[0.3em] text-secondary">{t("roadmap.section_label")}</p>
          <h2 className="mt-3 font-display font-black tracking-tighter text-5xl md:text-6xl leading-[0.95]">
            {t("roadmap.title_l1")}
            <br />
            <span className="text-primary neon-glow-primary">{t("roadmap.title_l2")}</span>
          </h2>
          <p className="mt-6 text-muted-foreground">{t("roadmap.subtitle")}</p>
        </motion.div>

        <div className="relative mt-16">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-primary/40 to-transparent md:-translate-x-px" />

          <div className="space-y-14">
            {phases.map((p, i) => {
              const leftSide = i % 2 === 0;
              const done = p.status_key === "done";
              return (
                <motion.div
                  key={p.phase}
                  data-testid={`roadmap-phase-${i}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-16 items-center`}
                >
                  <div className="absolute left-4 md:left-1/2 top-4 -translate-x-1/2 flex items-center justify-center">
                    <div className={`h-4 w-4 rounded-full ${done ? "bg-primary shadow-[0_0_20px_hsl(var(--primary))]" : "bg-black border-2 border-primary"}`} />
                  </div>

                  <div className={`${leftSide ? "md:order-1" : "md:order-2 md:col-start-2"}`}>
                    <div className="glass rounded-sm border border-primary/25 p-6 md:p-8 hover:border-primary/60 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="font-mono-alt text-[10px] tracking-widest text-secondary">{p.phase}</span>
                        <span className={`font-mono-alt text-[10px] tracking-widest px-2 py-0.5 rounded-sm ${done ? "bg-primary/20 text-primary" : "border border-muted-foreground/30 text-muted-foreground"}`}>
                          {t(`roadmap.status.${p.status_key}`)}
                        </span>
                      </div>
                      <h3 className="mt-3 font-display font-black text-2xl md:text-3xl tracking-tighter">{p.title}</h3>
                      <ul className="mt-4 space-y-2">
                        {p.items.map((it) => (
                          <li key={it} className="flex items-start gap-3 text-sm text-muted-foreground">
                            <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${done ? "bg-primary" : "bg-muted-foreground/40"}`} />
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className={`${leftSide ? "md:order-2" : "md:order-1"} hidden md:block`} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
